import { Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { Dispatch, FC, memo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, PlusIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { Radio, RadioGroup } from '../../../components/radio-group/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../components/select/index';
import { QueryKey } from '../../../constants/index';
import { AnalyticHooks, Company, useNavigate } from '../../../hooks/index';
import { filterVariant, FilterVariant, filterVariantMap } from '../../../types/company.types';
import { PossibleAction } from '../../../types/google-analytics-types.types';
import { PageRoute } from '../../../types/page.types';

export type MyCompaniesHeaderProps = {
  onFilterSelect?: () => void;
  selectedFilter: FilterVariant;
  setSelectedFilter: Dispatch<React.SetStateAction<FilterVariant>>;
};

const MyCompaniesHeader: FC<MyCompaniesHeaderProps> = memo(
  ({ setSelectedFilter, onFilterSelect, selectedFilter }) => {
    const queryClient = useQueryClient();

    const [selected, setSelected] = useState<FilterVariant>('all');
    const navigate = useNavigate(PageRoute.COMPANY_SETUP);
    const handleFilterSelect = (filter: FilterVariant) => {
      setSelectedFilter(filter);
      setSelected(filter);
      onFilterSelect?.();
    };

    const companyCreationTracker = AnalyticHooks.useClick(PossibleAction.ADD_COMPANY);

    const { create } = Company.useCreate();
    const { companies, isLoading: isLoadingCompanies } = Company.useCompanies({
      filter: 'all',
      numberOfFetchedItems: 1,
    });
    const areFiltersDisabled = !isLoadingCompanies && companies.length === 0;

    return (
      <div className="flex max-h-16 w-full items-center justify-between lg:p-4">
        <RadioGroup
          className="h-fit divide-x divide-gray-200 overflow-hidden rounded border border-gray-200"
          disabled={areFiltersDisabled}
          onChange={handleFilterSelect}
          value={selectedFilter}
          wrapperClassName={'max-lg:hidden h-9'}
        >
          {filterVariantMap.map(([filterKey, filter]) => (
            <Radio
              className={twMerge(
                'flex h-9 items-center justify-center px-4 text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]',
                areFiltersDisabled && 'cursor-not-allowed opacity-50',
              )}
              disabled={areFiltersDisabled}
              key={filterKey}
              styleType="NONE"
              value={filterKey}
            >
              <span>
                <AppFormattedMessage id={filter} />
              </span>
            </Radio>
          ))}
        </RadioGroup>
        <Select disabled={areFiltersDisabled} onChange={handleFilterSelect} value={selected}>
          {({ open }) => (
            <>
              <div
                className={twMerge(
                  'relative rounded border-gray-200 px-[6px] py-2 lg:hidden',
                  areFiltersDisabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <SelectButton>
                  <span className="text-sm font-[550] text-[#172335]">
                    <AppFormattedMessage id={filterVariant[selected]} />
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </SelectButton>

                <Transition
                  as="div"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  show={open}
                >
                  <SelectOptions>
                    {filterVariantMap.map(([filterKey, filter]) => (
                      <SelectOption key={`${filterKey}_m`} value={filterKey}>
                        {({ selected }) => (
                          <span
                            className={twMerge(
                              selected ? 'font-[550]' : 'font-normal',
                              'block text-sm text-gray-700',
                            )}
                          >
                            <AppFormattedMessage id={filter} />
                          </span>
                        )}
                      </SelectOption>
                    ))}
                  </SelectOptions>
                </Transition>
              </div>
            </>
          )}
        </Select>
        <Button
          className="w-fit gap-1 px-4 py-2 max-lg:size-9"
          onClick={() =>
            create(undefined, {
              onSuccess: ({ id }) => {
                companyCreationTracker();
                navigate([id]);
                queryClient.resetQueries({
                  predicate: (query) =>
                    ![QueryKey.GET_COMPANIES, QueryKey.GET_SELECTED_COMPANY].includes(
                      query.queryKey[0] as QueryKey,
                    ),
                });
              },
            })
          }
        >
          <div>
            <PlusIcon />
          </div>
          <span className="max-lg:hidden">
            <AppFormattedMessage id="ADD_NEW_COMPANY" />
          </span>
        </Button>
      </div>
    );
  },
);

MyCompaniesHeader.displayName = 'MyCompaniesHeader';

export default MyCompaniesHeader;
